<template>
    <div>
        <v-menu :disabled="disabled" v-model="menu" :close-on-content-click="false" offset-y>
            <template v-slot:activator="{ props }">
                <div v-bind="props" style="text-align: center">
                    <IconDetails v-if="source=='accountholder'"/>
                    <SettingDots v-else/>
                </div>
            </template>
            <v-card class="user-details-modal" :class="{ 'expanded': store.getters.isMonitoring && user?.SaasOperator }">
                <button type="button" class="close-modal" @click="closeMenu">
                    <IconClose />
                </button>

                <!-- Tab Navigation (only for Account Holder in monitoring mode) -->
                <div v-if="store.getters.isMonitoring && user?.SaasOperator" class="tab-navigation">
                    <button
                        @click="changeTab('profile')"
                        :class="{ 'active': currentTab === 'profile' }"
                        class="tab-button"
                    >
                        Profile
                    </button>
                    <button
                        @click="changeTab('messages')"
                        :class="{ 'active': currentTab === 'messages' }"
                        class="tab-button"
                    >
                        Messages
                    </button>
                    <button
                        @click="changeTab('activity')"
                        :class="{ 'active': currentTab === 'activity' }"
                        class="tab-button"
                    >
                        Activity
                    </button>
                </div>

                <!-- Operator Selector Dropdown (only for Account Holder in monitoring mode) -->
                <div v-if="store.getters.isMonitoring && user?.SaasOperator" class="operator-selector-dropdown">
                    <label>Select Operator:</label>
                    <select v-model="selectedOperatorForMonitoring" @change="onOperatorChange" class="operator-select">
                        <option v-for="op in operatorsList" :key="op.UserID" :value="op">
                            {{ op.FirstName }} {{ op.LastName }}
                        </option>
                    </select>
                </div>

                <!-- Profile Tab (existing content) -->
                <div v-show="currentTab === 'profile'" class="tab-content">
                    <div class="user-details-top">
                        <div class="user-details">
                            <div class="title">{{ (selectedOperatorForMonitoring || user)?.FirstName + " " + (selectedOperatorForMonitoring || user)?.LastName }} - Details</div>
                            <ul v-if="user.SaasOperator" :class="{ 'is-selectable': isDataTransferEnabled, 'is-selected': isDataTransferEnabled }"
                                class="user-selector">
                                <li v-if="user.SaasOperator">
                                    <IconContacts />Contacts:<span>{{ data?.contact ?? "0" }}</span>
                                </li>
                                <li v-if="user.SaasOperator">
                                    <IconGroup />Groups:<span>{{ data?.group ?? "0" }}</span>
                                </li>
                                <li v-if="user.SaasOperator">
                                    <IconMessages />Messages:<span>{{ data?.message ?? "0" }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="isDataTransferEnabled" class="receiver-details">
                            <div class="title">Select an Operator...</div>
                            <ul class="receiver-list">
                                <li v-for="(operator, index) in operators" :key="index" @click="selectReceiver(operator)"
                                    :class="{ 'active': selectedReceiver === operator }">
                                    {{ operator?.FirstName + " " + operator?.LastName }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="user-details-bottom mt-2" v-if="user?.SaasOperator">
                        <button @click="toggleDataTransfer()" type="button" class="enable-button">
                            <div class="icon">
                                <IconCheckboxChecked v-if="isDataTransferEnabled" />
                                <IconCheckboxUnchecked v-else />
                            </div>Enable Data Transfer
                        </button>
                        <div v-if="isDataTransferEnabled && selectedItem !== null" class="action-buttons">
                            <button @click="toggleDataTransfer();" class="cancel-btn" type="button">Cancel</button>
                            <button @click="confirmDataTransferModal=true" :class="{ 'disabled-btn': !selectedReceiver }"
                                class="transfer-data-btn" type="button">Transfer Data
                                <ArrowInsideButton />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Messages Tab (NEW) -->
                <div v-show="currentTab === 'messages'" class="tab-content messages-tab">
                    <div class="title">Messages by {{ (selectedOperatorForMonitoring || user)?.FirstName }}</div>
                    <div class="messages-list">
                        <div v-if="operatorMessages.length === 0" class="empty-state">
                            No messages created yet.
                        </div>
                        <div v-else class="messages-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Location</th>
                                        <th>Start Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="msg in operatorMessages.slice(0, 5)" :key="msg.ContentID">
                                        <td>{{ msg.Title }}</td>
                                        <td>{{ msg.Location }}</td>
                                        <td>{{ formatDate(msg.StartDate) }}</td>
                                        <td>{{ msg.Mode == 1 ? 'Draft' : 'Active' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="messages-count">
                                Showing {{ Math.min(5, operatorMessages.length) }} of {{ messagesPager.totalItems ?? operatorMessages.length }} messages
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Activity Tab (NEW) -->
                <div v-show="currentTab === 'activity'" class="tab-content activity-tab">
                    <div class="title">Activity Analytics</div>
                    <div class="activity-summary">
                        <div class="activity-card">
                            <div class="activity-label">Active messages</div>
                            <div class="activity-value text-blue">{{ activitySummary.active_messages ?? 0 }}</div>
                        </div>
                        <div class="activity-card">
                            <div class="activity-label">Receivers heard</div>
                            <div class="activity-value text-green">{{ activitySummary.receivers_heard ?? 0 }}</div>
                        </div>
                        <div class="activity-card">
                            <div class="activity-label">Not yet heard</div>
                            <div class="activity-value text-navy">{{ activitySummary.notyet_heard ?? 0 }}</div>
                        </div>
                    </div>
                    <div class="activity-history">
                        <div class="title">History</div>
                        <div class="history-row">
                            <div>Geofence Messages</div>
                            <div class="value">{{ activitySummary.geofence_messages ?? 0 }}</div>
                        </div>
                        <div class="history-row">
                            <div>Message plays</div>
                            <div class="value">{{ activitySummary.message_plays ?? 0 }}</div>
                        </div>
                        <div class="history-row">
                            <div>Receivers</div>
                            <div class="value">{{ activitySummary.receivers ?? 0 }}</div>
                        </div>
                        <div class="history-row">
                            <div>Communication Efficiency</div>
                            <div class="value">{{ activitySummary.communication_efficiency ?? 0 }}%</div>
                        </div>
                    </div>
                </div>
            </v-card>
        </v-menu>
    </div>

    <Confirmation 
    v-model="confirmDataTransferModal" 
    :data="null" 
    @closed="confirmDataTransferModal = false" 
    :onconfirm="() => transferData()"
    :body="`
        Are you sure you want to transfer this Operator Data? <br><br>
        This Data will be managed by another Operator.<br><br>
        This Data transfer cannot be undone.<br><br>
    `" 
  />
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, onMounted } from 'vue';
import SettingDots from '@/components/icons/message_settings/SettingDots.vue';
import IconContacts from '@/components/icons/IconContacts.vue';
import IconGroup from '@/components/icons/IconGroup.vue';
import IconMessages from '@/components/icons/IconMessages.vue';
import IconOperator from '@/components/icons/IconOperator.vue';
import IconCheckboxUnchecked from '@/components/icons/IconCheckboxUnchecked.vue';
import IconCheckboxChecked from '@/components/icons/IconCheckboxChecked.vue';
import IconClose from '@/components/icons/IconClose.vue';
import ArrowInsideButton from "@/components/icons/ArrowInsideButton.vue";
import userService from '@/services/userService';
import { useStore } from 'vuex';
import Confirmation from '../modals/Confirmation.vue';
import IconDetails from '../icons/IconDetails.vue';

let props = defineProps(['content', 'item','source']);
let emits = defineEmits(['item-selected', 'success','error']);
let menu = ref(false);
let disabled = ref(props.disabled);
let user = computed(()=>props.item);
let store = useStore();
let currentUser = computed(() => store.getters.user);
let data = ref({});
let confirmDataTransferModal = ref(false);

// Operator monitoring state
const currentTab = ref('profile'); // 'profile', 'messages', 'activity'
const operatorMessages = ref([]);
const messagesPager = ref({});
const messagesCurrentPage = ref(1);
const activitySummary = ref({});
const isAccountHolder = computed(() => currentUser.value?.AccountHolder);
const operatorsList = ref([]); // List of all operators
const selectedOperatorForMonitoring = ref(null); // Currently selected operator for monitoring


const closeMenu = () => {
    menu.value = false;
    confirmDataTransferModal.value=false;
};

let operators = ref([]);

let isDataTransferEnabled = ref(false);
const selectedReceiver = ref(null);

function selectReceiver(receiver) {
    selectedReceiver.value = receiver;
}

async function getUserProfileData() {
    const clientId = store.getters.isOnBehalf
        ? store.getters.onBehalfUser?.ClientID
        : store.getters.user?.ClientID;
    data.value = await userService.getUserProfileData(user.value?.UserID, clientId);
}

function toggleDataTransfer() {
    isDataTransferEnabled.value = !isDataTransferEnabled.value;
    if(isDataTransferEnabled.value){
        getOperators();
    }
}

async function getOperators() {
    let ops = await userService.getOperators();
    operators.value = ops.filter(op=>op.UserID!=user?.value?.UserID)
}

async function transferData() {
    try {
        let res = await userService.transferData(user.value?.UserID, selectedReceiver.value?.UserID);
        toggleDataTransfer();
        emits("success","Data transferred successfully!")
    } catch (error) {
        emits('error',error?.message)
    }
    closeMenu();

}

// Load list of operators for dropdown
async function loadOperatorsList() {
  try {
    const ops = await userService.getFilteredOperators();
    operatorsList.value = ops;
  } catch (error) {
    console.error('Failed to load operators list:', error);
  }
}

// Fetch operator messages
async function getOperatorMessages() {
  const operatorToView = selectedOperatorForMonitoring.value || user.value;
  if (!operatorToView?.UserID) return;

  try {
    const clientId = store.getters.isOnBehalf
      ? store.getters.onBehalfUser?.ClientID
      : store.getters.user?.ClientID;

    const url = `${process.env.VUE_APP_APIDOMAIN}/ourcontents?` +
      `ClientID=${clientId}&` +
      `SenderID=${operatorToView.UserID}&` +
      `page=${messagesCurrentPage.value}&` +
      `perpage=10`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('tknds')
      }
    });

    const result = await response.json();

    if (result.AuthSuccess === 0) {
      store.dispatch('logout');
    } else {
      operatorMessages.value = result.returnData || [];
      messagesPager.value = result.pager || {};
    }
  } catch (error) {
    console.error('Failed to load operator messages:', error);
  }
}

// Fetch operator activity analytics
async function getOperatorActivity() {
  const operatorToView = selectedOperatorForMonitoring.value || user.value;
  if (!operatorToView?.UserID) return;

  try {
    const clientId = store.getters.isOnBehalf
      ? store.getters.onBehalfUser?.ClientID
      : store.getters.user?.ClientID;

    const now = new Date();
    const queryTime = getDateForSql(now);

    const summaryUrl = `${process.env.VUE_APP_APIDOMAIN}/getactivitysummary?` +
      `UserID=${operatorToView.UserID}&` +
      `ClientID=${clientId}&` +
      `Role=Operator&` +
      `QueryTime=${queryTime}&` +
      `day=0`;

    const response = await fetch(summaryUrl, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('tknds')
      }
    });

    activitySummary.value = await response.json();
  } catch (error) {
    console.error('Failed to load activity data:', error);
  }
}

// Helper function for date formatting
function getDateForSql(dateTime) {
  const year = dateTime.getFullYear();
  const month = ('0' + (dateTime.getMonth() + 1)).slice(-2);
  const day = ('0' + dateTime.getDate()).slice(-2);
  const hours = ('0' + dateTime.getHours()).slice(-2);
  const minutes = ('0' + dateTime.getMinutes()).slice(-2);
  const seconds = ('0' + dateTime.getSeconds()).slice(-2);
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Format date for display
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

// Tab change handler
function changeTab(tab) {
  currentTab.value = tab;
  if (tab === 'messages' && operatorMessages.value.length === 0) {
    getOperatorMessages();
  } else if (tab === 'activity' && !activitySummary.value.active_messages) {
    getOperatorActivity();
  }
}

// Handler when operator is changed from dropdown
async function onOperatorChange() {
  if (!selectedOperatorForMonitoring.value) return;

  // Reload all monitoring data for the newly selected operator
  await Promise.all([
    getUserProfileData(),
    getOperatorMessages(),
    getOperatorActivity()
  ]);
}

// Update getUserProfileData to use selected operator
async function getUserProfileDataForOperator(operatorUser) {
    const clientId = store.getters.isOnBehalf
        ? store.getters.onBehalfUser?.ClientID
        : store.getters.user?.ClientID;
    data.value = await userService.getUserProfileData(operatorUser?.UserID, clientId);
}

watch(menu, async (val) => {
    if (val) {
        console.log('🔍 [UserDetails] Modal opened');
        console.log('🔍 [UserDetails] Current user:', currentUser.value);
        console.log('🔍 [UserDetails] Is Account Holder?:', isAccountHolder.value);
        console.log('🔍 [UserDetails] Clicked user:', user.value);
        console.log('🔍 [UserDetails] Is clicked user an Operator?:', user.value?.SaasOperator);

        getUserProfileData();
        // Load monitoring data for Account Holder in monitoring mode
        if (store.getters.isMonitoring && user.value?.SaasOperator) {
            console.log('✅ [UserDetails] Loading monitoring features!');
            currentTab.value = 'profile';
            // Initialize selected operator to the clicked operator
            selectedOperatorForMonitoring.value = user.value;
            // Load operators list for dropdown
            await loadOperatorsList();
            // Load monitoring data
            getOperatorMessages();
            getOperatorActivity();
        } else {
            console.log('❌ [UserDetails] NOT loading monitoring features');
            console.log('   - isAccountHolder:', isAccountHolder.value);
            console.log('   - user?.SaasOperator:', user.value?.SaasOperator);
        }
    } else {
        // Reset tab and selected operator when closing
        currentTab.value = 'profile';
        selectedOperatorForMonitoring.value = null;
    }
})
</script>
<style lang="scss" scoped>
.user-details-modal {
    width: 600px;
    height: 350px !important;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    & .close-modal {
        position: absolute;
        right: 10px;
        top: 10px;
    }

    & .user-details-top {
        display: grid;
        grid-template-columns: 250px 250px;
        gap: 60px;

        & .user-details {
            display: flex;
            flex-direction: column;

            & .title {
                font-family: Roboto;
                font-size: 16px;
                font-weight: 500;
                line-height: 16px;
                text-align: left;
                margin-bottom: 18px;
                color: #333333;
            }

            & .user-selector {
                display: flex;
                flex-direction: column;
                padding: 10px;
                gap: 18px;
                font-size: 16px;
                line-height: 1;
                font-weight: 500;
                border-radius: 5px;
                margin-bottom: 15px;
                height: 150px;
                border: 1.5px solid transparent;

                &.is-selectable {
                    border: 1.5px solid #D9D9D9;
                    cursor: pointer;
                }

                &.is-selected {
                    background-color: #F2F2F2;
                }

                & li {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #828282;
                    font-size: 16px;
                    line-height: 1;
                    font-weight: 500;

                    & span {
                        color: #344D7A;
                    }
                }
            }

            & .operator-selector {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 10px;
                font-size: 16px;
                line-height: 1;
                font-weight: 500;
                border-radius: 5px;
                color: #828282;
                height: 50px;
                border: 1.5px solid transparent;

                &.is-selectable {
                    border: 1.5px solid #D9D9D9;
                    cursor: pointer;
                }

                &.is-selected {
                    background-color: #F2F2F2;
                }

                & span {
                    color: #344D7A;
                }
            }
        }

        & .receiver-details {
            display: flex;
            flex-direction: column;

            & .title {
                font-family: Roboto;
                font-size: 16px;
                font-weight: 400;
                line-height: 16px;
                text-align: left;
                margin-bottom: 18px;
                color: #333333;
            }

            & .receiver-list {
                border: 1.5px solid #D9D9D9;
                border-radius: 5px;
                display: flex;
                flex-direction: column;
                padding-left: 0;
                height: 150px;
                overflow: auto;

                & li {
                    display: flex;
                    align-items: center;
                    padding: 3px 15px;
                    font-family: Roboto;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 28px;
                    text-align: center;
                    color: #333333;
                    transition: 0.2s all ease;
                    cursor: pointer;

                    &.active {
                        background-color: #EAF2FC;
                    }

                    &:hover {
                        background-color: #EAF2FC;
                    }
                }
            }
        }
    }

    & .user-details-bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 12px;
        height: 40px;


        & .enable-button {
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: Roboto;
            font-size: 16px;
            font-weight: 400;
            line-height: 16px;
            text-align: left;
            color: #000;

            & .icon {
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }

        & .action-buttons {
            display: flex;
            align-items: center;
            gap: 58px;

            & .cancel-btn {
                font-family: Roboto;
                font-size: 14px;
                font-weight: 500;
                line-height: 16.41px;
                text-align: right;
                color: #2F80ED;
                text-transform: uppercase
            }

            & .transfer-data-btn {
                width: 139px;
                height: 40px;
                display: flex;
                align-items: center;
                gap: 5px;
                padding: 8px 6px;
                justify-content: flex-end;
                background-color: #4066CF;
                border: 1px solid #4066CF;
                font-family: Roboto;
                font-size: 14px;
                font-weight: 500;
                line-height: 20px;
                letter-spacing: 0.10000000149011612px;
                text-align: left;
                color: white;
                border-radius: 5px;
                transition: 0.2s all ease;

                &.disabled-btn {
                    border-color: #BDBDBD;
                    color: #BDBDBD;
                    pointer-events: none;
                    background-color: white;
                }

                &:hover {
                    border-color: #6B87D4;
                    background-color: #6B87D4;
                }
            }
        }

    }

}

// Expanded modal for Account Holder monitoring
.user-details-modal.expanded {
  width: 800px;
  height: 500px !important;
}

// Tab navigation
.tab-navigation {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #E0E0E0;
  margin-bottom: 10px;

  .tab-button {
    padding: 12px 24px;
    background: none;
    border: none;
    font-size: 16px;
    font-weight: 500;
    color: #828282;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    bottom: -2px;

    &:hover {
      color: #4066CF;
    }

    &.active {
      color: #4066CF;
      border-bottom-color: #4066CF;
    }
  }
}

// Operator selector dropdown
.operator-selector-dropdown {
  padding: 12px 0 16px;
  border-bottom: 1px solid #E0E0E0;
  margin-bottom: 16px;

  label {
    display: inline-block;
    font-size: 14px;
    font-weight: 500;
    color: #333333;
    margin-right: 12px;
  }

  .operator-select {
    min-width: 200px;
    height: 36px;
    padding: 6px 12px;
    border: 1px solid #BDBDBD;
    border-radius: 5px;
    font-size: 14px;
    color: #333333;
    background-color: #fff;
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: #828282;
    }

    &:focus {
      outline: none;
      border-color: #4066CF;
    }
  }
}

// Tab content
.tab-content {
  flex: 1;
  overflow-y: auto;

  .title {
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    margin-bottom: 16px;
  }
}

// Messages tab
.messages-tab {
  .messages-list {
    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: #828282;
      font-size: 14px;
    }

    .messages-table {
      table {
        width: 100%;
        border-collapse: collapse;

        thead {
          background-color: #F2F2F2;

          th {
            padding: 12px;
            text-align: left;
            font-size: 14px;
            font-weight: 500;
            color: #333333;
            border-bottom: 2px solid #E0E0E0;
          }
        }

        tbody {
          tr {
            border-bottom: 1px solid #E0E0E0;
            transition: background-color 0.2s ease;

            &:hover {
              background-color: #F9F9F9;
            }

            td {
              padding: 12px;
              font-size: 14px;
              color: #333333;
            }
          }
        }
      }

      .messages-count {
        margin-top: 12px;
        text-align: right;
        font-size: 13px;
        color: #828282;
      }
    }
  }
}

// Activity tab
.activity-tab {
  .activity-summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 30px;

    .activity-card {
      background-color: #F9F9F9;
      border-radius: 8px;
      padding: 20px;
      text-align: center;

      .activity-label {
        font-size: 14px;
        color: #828282;
        margin-bottom: 12px;
      }

      .activity-value {
        font-size: 36px;
        font-weight: 300;
        line-height: 1;
      }

      .text-blue {
        color: #4992E6;
      }

      .text-green {
        color: #25AC1E;
      }

      .text-navy {
        color: #344D7A;
      }
    }
  }

  .activity-history {
    background-color: #F9F9F9;
    border-radius: 8px;
    padding: 20px;

    .title {
      font-size: 14px;
      font-weight: 500;
      color: #333333;
      margin-bottom: 16px;
    }

    .history-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #E0E0E0;
      font-size: 14px;
      color: #333333;

      &:last-child {
        border-bottom: none;
      }

      .value {
        font-size: 18px;
        font-weight: 500;
        color: #344D7A;
      }
    }
  }
}
</style>